import { useState, useEffect, useRef } from "react";
import { X, MapPin, ChevronRight, Zap, Star, Users } from "lucide-react";
import { SUPPORT_PHONE } from "@/lib/brand";
import api from "@/lib/api";
const TABS = [
  { id: "highlights", label: "Highlights", icon: Zap },
  { id: "bestMoments", label: "Best Moments", icon: Star },
  { id: "perfectFor", label: "Perfect For", icon: Users },
];

export default function AdventuresSection() {
  const [selected, setSelected] = useState(null);
  const [adventures, setAdventures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/adventures")
      .then((res) => {
        setAdventures(res.data);
      })
      .catch((err) => {
        console.error("Failed to load adventures", err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  // Lock scroll when modal open
  useEffect(() => {
    document.body.style.overflow = selected ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [selected]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setSelected(null); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  return (
    <>
      <section className="bg-stone-50 py-16 my-20" data-testid="adventures-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {/* Header */}
          <div className="mb-10">
            <h2 className="font-display text-3xl md:text-4xl mb-2">
              One Light Stays Adventures
            </h2>
            <p className="text-stone-600">
              Book all these adventures too with One light Stays.
            </p>
          </div>

          {/* Adventure Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="aspect-[4/5] bg-stone-200 animate-pulse rounded-2xl"></div>
              ))
            ) : adventures.length === 0 ? (
              <div className="col-span-full py-10 text-center text-stone-500">No adventures found.</div>
            ) : (
              adventures.map((adv) => (
                <AdventureCard key={adv.id} adv={adv} onClick={() => setSelected(adv)} />
              ))
            )}
          </div>
        </div>
      </section>

      {/* Detail Modal */}
      {selected && (
        <AdventureModal adv={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
}

function AdventureCard({ adv, onClick }) {
  return (
    <button
      onClick={onClick}
      aria-label={`Explore ${adv.title}`}
      className="group relative text-left rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-stone-900"
      data-testid={`adventure-card-${adv.id}`}
    >
      {/* Image */}
      <div className="aspect-[4/5] overflow-hidden bg-stone-200">
        <img
          src={adv.image}
          alt={adv.title}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-108 transition-transform duration-700"
          style={{ "--tw-scale-x": "1.08", "--tw-scale-y": "1.08" }}
        />
      </div>

      {/* Permanent gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

      {/* Text at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="flex items-end justify-between gap-2">
          <div>
            <div className="flex items-center gap-1 text-white/60 text-xs mb-0.5">
              <MapPin className="w-3 h-3" />
              <span>{adv.tagline}</span>
            </div>
            <div className="font-display text-xl text-white">{adv.title}</div>
          </div>
          {/* Explore arrow */}
          <div className="w-8 h-8 rounded-full bg-white/15 border border-white/30 flex items-center justify-center group-hover:bg-white group-hover:border-white transition-all duration-300 shrink-0">
            <ChevronRight className="w-4 h-4 text-white group-hover:text-stone-900 transition-colors duration-300" />
          </div>
        </div>

        {/* Description — slides up on hover */}
        <p className="text-white/75 text-xs leading-relaxed mt-2 line-clamp-2 max-h-0 overflow-hidden group-hover:max-h-20 transition-all duration-500 ease-out">
          {adv.description}
        </p>
      </div>
    </button>
  );
}

function AdventureModal({ adv, onClose }) {
  const [tab, setTab] = useState("highlights");
  const overlayRef = useRef(null);

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ animation: "ols-fade-up 0.25s cubic-bezier(0.22,1,0.36,1) both" }}
      data-testid="adventure-modal"
    >
      <div className="relative bg-white w-full sm:max-w-2xl sm:rounded-2xl overflow-hidden max-h-[92vh] flex flex-col shadow-2xl">
        {/* Hero image */}
        <div className="relative h-52 shrink-0 overflow-hidden bg-stone-200">
          <img
            src={adv.image}
            alt={adv.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* Close button */}
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/30 hover:bg-black/60 border border-white/30 flex items-center justify-center text-white transition-all duration-200"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Title over image */}
          <div className="absolute bottom-4 left-5">
            <div className="flex items-center gap-1.5 text-white/70 text-xs mb-1">
              <MapPin className="w-3.5 h-3.5" />
              <span>{adv.tagline}</span>
            </div>
            <h2 className="font-display text-3xl text-white">{adv.title}</h2>
          </div>
        </div>

        {/* Description */}
        <div className="px-5 pt-4 pb-2 shrink-0 border-b border-stone-100">
          <p className="text-stone-600 text-sm leading-relaxed">{adv.description}</p>
        </div>

        {/* Tabs */}
        <div className="px-5 pt-3 shrink-0 border-b border-stone-100">
          <div className="flex gap-0">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold border-b-2 transition-all duration-200 ${tab === id
                  ? "border-stone-900 text-stone-900"
                  : "border-transparent text-stone-400 hover:text-stone-600"
                  }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab content — scrollable */}
        <div className="overflow-y-auto flex-1 px-5 py-5">
          {tab === "highlights" && (
            <ul className="space-y-2.5">
              {adv.highlights.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-stone-900 text-white flex items-center justify-center shrink-0 mt-0.5 text-[0.6rem] font-bold">
                    {i + 1}
                  </div>
                  <span className="text-stone-700 text-sm leading-snug">{item}</span>
                </li>
              ))}
            </ul>
          )}

          {tab === "bestMoments" && (
            <ul className="space-y-2.5">
              {adv.bestMoments.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-stone-900 text-white flex items-center justify-center shrink-0 mt-0.5 text-[0.6rem] font-bold">
                    {i + 1}
                  </div>
                  <span className="text-stone-700 text-sm leading-snug">{item}</span>
                </li>
              ))}
            </ul>
          )}

          {tab === "perfectFor" && (
            <ul className="space-y-2.5">
              {adv.perfectFor.map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-5 h-5 rounded-full bg-stone-900 text-white flex items-center justify-center shrink-0 mt-0.5 text-[0.6rem] font-bold">
                    {i + 1}
                  </div>
                  <span className="text-stone-700 text-sm leading-snug">{item}</span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Footer CTA */}
        <div className="shrink-0 px-5 py-4 border-t border-stone-100 bg-stone-50 flex items-center justify-between gap-3">
          <p className="text-xs text-stone-500">
            {adv.bottomText}
          </p>
          <a
            href={`tel:${SUPPORT_PHONE}`}
            className="btn-primary text-xs whitespace-nowrap"
          >
            Enquire Now
          </a>
        </div>
      </div>
    </div>
  );
}
